// path: frontend/src/lib/api-config.ts
/**
 * API Configuration
 * Centralized configuration for all API calls.
 */

// Raw value from environment (may be undefined/empty)
const rawApiBase =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    (import.meta.env.VITE_API_BASE as string | undefined) ??
    (import.meta.env.VITE_API_URL as string | undefined) ??
    "";

// Normalize: trim whitespace and remove trailing slashes
const normalizedApiBase = rawApiBase.toString().trim().replace(/\/+$/, "");

/**
 * Base URL used by the frontend for all API calls.
 *
 * Production: https://api.autoquizai.com
 * Development: http://localhost:8000 (or empty for relative URLs)
 */
export const API_BASE_URL: string = normalizedApiBase;

/**
 * Default API base URL for production
 */
export const PRODUCTION_API_URL = "https://api.autoquizai.com";

/**
 * Get the effective API base URL
 * Falls back to production URL if not explicitly set
 */
export function getApiBaseUrl(): string {
    // If explicitly set in environment, use it
    if (API_BASE_URL) {
        return API_BASE_URL;
    }

    // In production build without explicit API_BASE_URL, use production API
    if (import.meta.env.PROD) {
        return PRODUCTION_API_URL;
    }

    // In development without explicit API_BASE_URL, use relative URLs (proxy)
    return "";
}

// Log configuration in development only (no secrets are exposed here)
if (import.meta.env.DEV) {
    console.log("🔧 API Configuration:", {
        baseUrl: getApiBaseUrl() || "(empty - using relative URLs)",
        mode: import.meta.env.MODE,
        prod: import.meta.env.PROD,
    });
}

/**
 * Build a full API URL from a path.
 * @param path - API path (e.g., "/api/video/generate" or "api/video/generate")
 * @returns Full URL (absolute if API_BASE_URL set, otherwise relative)
 */
export function buildApiUrl(path: string): string {
    // Normalize path: remove all leading slashes
    const cleanPath = path.replace(/^\/+/, "");

    const baseUrl = getApiBaseUrl();

    // If no API_BASE_URL, use relative URL (same origin as frontend)
    if (!baseUrl) {
        return `/${cleanPath}`;
    }

    return `${baseUrl}/${cleanPath}`;
}

/**
 * Check if API base URL is explicitly configured.
 */
export function isApiConfigured(): boolean {
    return Boolean(getApiBaseUrl());
}